import { v7 as uuidv7 } from "uuid";
import {
  buildStudioBlobPath,
  fileExtension,
  isAllowedStudioContentImageExtension,
  isAllowedStudioOgImageExtension,
  isAllowedStudioUploadExtension,
  parseStudioUploadPurpose,
  sanitizeFilename,
  sanitizeSegment,
  STUDIO_CONTENT_IMAGE_ALLOWED_CONTENT_TYPES,
  STUDIO_CONTENT_IMAGE_MAX_BYTES,
  STUDIO_OG_IMAGE_ALLOWED_CONTENT_TYPES,
  STUDIO_OG_IMAGE_MAX_BYTES,
  STUDIO_UPLOAD_ALLOWED_CONTENT_TYPES,
  STUDIO_UPLOAD_MAX_BYTES,
  type StudioUploadPurpose,
} from "@/utils/studioBlobPath";

export type StudioUploadResult = {
  url: string;
  pathname: string;
  resourceId: string;
  filename: string;
};

export type StudioUploadClientPayload = {
  pageId: string;
  resourceId: string;
  filename: string;
  fileSize: number;
  uploadPurpose?: StudioUploadPurpose;
};

function maxBytesForPurpose(purpose: StudioUploadPurpose): number {
  if (purpose === "og-image") {
    return STUDIO_OG_IMAGE_MAX_BYTES;
  }
  if (purpose === "content-image") {
    return STUDIO_CONTENT_IMAGE_MAX_BYTES;
  }
  return STUDIO_UPLOAD_MAX_BYTES;
}

function isAllowedExtensionForPurpose(
  ext: string,
  purpose: StudioUploadPurpose,
): boolean {
  if (purpose === "og-image") {
    return isAllowedStudioOgImageExtension(ext);
  }
  if (purpose === "content-image") {
    return isAllowedStudioContentImageExtension(ext);
  }
  return isAllowedStudioUploadExtension(ext);
}

function contentTypesForPurpose(purpose: StudioUploadPurpose): string[] {
  if (purpose === "og-image") {
    return STUDIO_OG_IMAGE_ALLOWED_CONTENT_TYPES;
  }
  if (purpose === "content-image") {
    return STUDIO_CONTENT_IMAGE_ALLOWED_CONTENT_TYPES;
  }
  return STUDIO_UPLOAD_ALLOWED_CONTENT_TYPES;
}

export function validateStudioUploadFileMeta(
  size: number,
  filename: string,
  purpose: StudioUploadPurpose = "page-file",
): string | null {
  if (size > maxBytesForPurpose(purpose)) {
    return "file_too_large";
  }

  const ext = fileExtension(filename);
  if (!ext || !isAllowedExtensionForPurpose(ext, purpose)) {
    return "unsupported_file_type";
  }

  return null;
}

export function buildStudioUploadTarget(
  pageId: string,
  fileName: string,
  fileSize: number,
  resourceId?: string,
  purpose: StudioUploadPurpose = "page-file",
): StudioUploadClientPayload & { pathname: string } {
  const validationError = validateStudioUploadFileMeta(
    fileSize,
    fileName,
    purpose,
  );
  if (validationError) {
    throw new Error(validationError);
  }

  const safePageId = sanitizeSegment(pageId) || "shared";
  const id =
    resourceId?.trim() ?
      sanitizeSegment(resourceId.trim(), 40)
    : uuidv7();
  const filename = sanitizeFilename(fileName);
  const pathname = buildStudioBlobPath({
    pageId: safePageId,
    resourceId: id,
    filename,
  });

  return {
    pageId: safePageId,
    resourceId: id,
    filename,
    fileSize,
    uploadPurpose: purpose,
    pathname,
  };
}

export function parseStudioUploadClientPayload(
  raw: string | null,
): StudioUploadClientPayload | null {
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<StudioUploadClientPayload>;
    if (
      typeof parsed.pageId !== "string" ||
      typeof parsed.resourceId !== "string" ||
      typeof parsed.filename !== "string" ||
      typeof parsed.fileSize !== "number"
    ) {
      return null;
    }

    return {
      pageId: parsed.pageId,
      resourceId: parsed.resourceId,
      filename: parsed.filename,
      fileSize: parsed.fileSize,
      uploadPurpose: parseStudioUploadPurpose(parsed.uploadPurpose),
    };
  } catch {
    return null;
  }
}

export function assertStudioUploadPathname(
  pathname: string,
  clientPayload: string | null,
): StudioUploadClientPayload {
  const payload = parseStudioUploadClientPayload(clientPayload);
  if (!payload) {
    throw new Error("invalid_client_payload");
  }

  const expected = buildStudioBlobPath({
    pageId: payload.pageId,
    resourceId: payload.resourceId,
    filename: payload.filename,
  });

  if (pathname !== expected || !pathname.startsWith("studio/")) {
    throw new Error("invalid_upload_path");
  }

  const purpose = payload.uploadPurpose ?? "page-file";
  const validationError = validateStudioUploadFileMeta(
    payload.fileSize,
    payload.filename,
    purpose,
  );
  if (validationError) {
    throw new Error(validationError);
  }

  return payload;
}

export function getStudioUploadTokenOptions(
  pathname: string,
  clientPayload: string | null,
) {
  const payload = assertStudioUploadPathname(pathname, clientPayload);
  const purpose = payload.uploadPurpose ?? "page-file";

  return {
    allowedContentTypes: contentTypesForPurpose(purpose),
    maximumSizeInBytes: maxBytesForPurpose(purpose),
    addRandomSuffix: false,
    tokenPayload: JSON.stringify({
      pageId: payload.pageId,
      resourceId: payload.resourceId,
      filename: payload.filename,
    }),
  };
}
